import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Store, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function Vendor() {
  const [listings, setListings] = useState([]);
  const [history, setHistory] = useState([]);
  const [listing, setListing] = useState({ name: "", category: "equipment", description: "", daily_rate: 0, coverage_city: "" });
  const [push, setPush] = useState({ headline: "", body: "" });

  const reload = async () => {
    const [l, h] = await Promise.all([
      api.get("/vendor/listings").then((r) => r.data).catch(() => []),
      api.get("/vendor/push/history").then((r) => r.data).catch(() => []),
    ]);
    setListings(l); setHistory(h);
  };
  useEffect(() => { reload(); }, []);

  const createListing = async (e) => {
    e.preventDefault();
    try {
      await api.post("/vendor/listings", listing);
      toast.success("Listing added");
      setListing({ ...listing, name: "", description: "" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const sendPush = async () => {
    try {
      const { data } = await api.post("/vendor/push", {
        headline: push.headline, body: push.body,
        target_tiers: ["direction", "creative"],
      });
      toast.success(`Push delivered to ~${data.recipients_estimated} recipients`);
      setPush({ headline: "", body: "" });
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10" data-testid="vendor-page">
      <div className="mb-8">
        <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Vendor Console</div>
        <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2">Catalog + Monthly Push</h1>
        <p className="mt-3 text-sm text-white/55 max-w-2xl flex items-center gap-2">
          <AlertTriangle size={14} className="text-[#F5B82E]" />
          1 promotional blast per calendar month. Labor tier is automatically shielded.
        </p>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Listing */}
        <form onSubmit={createListing} className="cs-card rounded-2xl p-6 space-y-4">
          <div className="font-display text-xl uppercase tracking-tight">Add Catalog Listing</div>
          <Field label="Name">
            <Input value={listing.name} required data-testid="v-name"
              onChange={(e) => setListing({ ...listing, name: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Category">
            <Input value={listing.category} data-testid="v-cat"
              onChange={(e) => setListing({ ...listing, category: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Coverage City">
            <Input value={listing.coverage_city} data-testid="v-city"
              onChange={(e) => setListing({ ...listing, coverage_city: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Daily Rate (INR)">
            <Input type="number" value={listing.daily_rate} data-testid="v-rate"
              onChange={(e) => setListing({ ...listing, daily_rate: parseFloat(e.target.value) || 0 })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Description">
            <Textarea value={listing.description} data-testid="v-desc"
              onChange={(e) => setListing({ ...listing, description: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Button type="submit" className="btn-themed w-full h-11 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="v-submit">
            <Store size={14} className="mr-2" /> Add Listing
          </Button>

          <div className="mt-6 pt-5 border-t border-white/5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Active listings</div>
            <div className="mt-3 space-y-2">
              {listings.length === 0 && <div className="text-xs text-white/40">No listings.</div>}
              {listings.map((l) => (
                <div key={l.id} className="flex justify-between border border-white/5 rounded-lg px-3 py-2 text-sm">
                  <div>{l.name} <span className="text-white/40">· {l.category}</span></div>
                  <div className="text-white/60 tabular-nums">₹{l.daily_rate}</div>
                </div>
              ))}
            </div>
          </div>
        </form>

        {/* Push composer */}
        <div className="cs-card rounded-2xl p-6 space-y-4">
          <div className="font-display text-xl uppercase tracking-tight">Monthly Promo Blast</div>
          <Field label="Headline">
            <Input value={push.headline} data-testid="push-headline"
              onChange={(e) => setPush({ ...push, headline: e.target.value })}
              className="bg-black/40 border-white/15" />
          </Field>
          <Field label="Body">
            <Textarea value={push.body} data-testid="push-body"
              onChange={(e) => setPush({ ...push, body: e.target.value })}
              className="bg-black/40 border-white/15 min-h-[100px]" />
          </Field>
          <div className="text-[10px] uppercase tracking-[0.25em] text-white/50">
            Audience: Direction tier + Creative tier · Labor tier shielded
          </div>
          <Button onClick={sendPush} className="btn-themed w-full h-11 rounded-full uppercase tracking-[0.25em] text-xs" data-testid="push-send">
            <Send size={14} className="mr-2" /> Send (consume monthly token)
          </Button>

          <div className="mt-6 pt-5 border-t border-white/5">
            <div className="text-[10px] tracking-[0.3em] uppercase text-white/50">Push history</div>
            <div className="mt-3 space-y-2 max-h-60 overflow-auto">
              {history.length === 0 && <div className="text-xs text-white/40">No pushes yet.</div>}
              {history.map((h) => (
                <div key={h.id} className="border border-white/5 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <div className="text-sm">{h.headline}</div>
                    <div className="text-[10px] uppercase tracking-[0.2em] text-white/40">{h.month_key}</div>
                  </div>
                  <div className="text-xs text-white/55 mt-1">{h.body}</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-white/40 mt-1">
                    ~{h.recipients_estimated} recipients · {h.target_tiers.join(", ")}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] tracking-[0.25em] uppercase text-white/60">{label}</Label>
      <div className="mt-1.5">{children}</div>
    </div>
  );
}
